"use client"

import { useState, useEffect } from "react"
import { UserPlus, Check, Loader2 } from "lucide-react"

interface ConnectButtonProps {
  recipientId: string
  initialStatus: string | null
}

export default function ConnectButton({ recipientId, initialStatus }: ConnectButtonProps) {
  const [status, setStatus] = useState<string | null>(initialStatus)
  const [isLoading, setIsLoading] = useState(false)
  const [isChecking, setIsChecking] = useState(!initialStatus)

  // Check connection status if not provided
  useEffect(() => {
    if (!initialStatus) {
      checkConnectionStatus()
    }
  }, [initialStatus])

  const checkConnectionStatus = async () => {
    setIsChecking(true)
    try {
      const response = await fetch(`/api/connections/check?recipientId=${recipientId}`)
      const data = await response.json()

      if (response.ok && data.exists) {
        setStatus(data.status)
      }
    } catch (error) {
      console.error("Error checking connection status:", error)
    } finally {
      setIsChecking(false)
    }
  }

  const sendConnectionRequest = async () => {
    if (isLoading) return

    setIsLoading(true)

    try {
      const response = await fetch("/api/connections/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ recipientId }),
      })

      const data = await response.json()

      if (response.ok) {
        setStatus("pending")
      } else {
        console.error("Connection request failed:", data.message)
        // If connection already exists, update the status
        if (response.status === 400 && data.status) {
          setStatus(data.status)
        }
      }
    } catch (error) {
      console.error("Error sending connection request:", error)
    } finally {
      setIsLoading(false)
    }
  }

  if (isChecking) {
    return (
      <button
        disabled
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400"
      >
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Checking...
      </button>
    )
  }

  if (isLoading) {
    return (
      <button
        disabled
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400"
      >
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Connecting...
      </button>
    )
  }

  if (status === "pending") {
    return (
      <button
        disabled
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-gray-100"
      >
        <Check className="h-4 w-4 mr-2" />
        Request Sent
      </button>
    )
  }

  if (status === "accepted") {
    return (
      <button
        disabled
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-green-700 bg-green-50"
      >
        <Check className="h-4 w-4 mr-2" />
        Connected
      </button>
    )
  }

  return (
    <button
      onClick={sendConnectionRequest}
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
    >
      <UserPlus className="h-4 w-4 mr-2" />
      Connect
    </button>
  )
}

